/**
 * Hauler-wide load-email settings on /settings (Phase 17 / Task 17.3, DEC-324 D-03, D-08,
 * D-12, D-13). TENANT_ADMIN only (MANAGE_TENANT on the API).
 *
 * - The DEFAULT "email after each haul" and summary schedule, which every customer follows
 *   unless that customer has its own setting. Both ship off (D-03).
 * - The office email and phone: replies to load emails go to that email, and both appear
 *   in the email footer (D-08). Without an office email, a reply has nowhere useful to go,
 *   so the section says so.
 * - The requestor switch (D-13): email the requestor ticked on a job when their load
 *   completes.
 *
 * Toggles and selects save immediately; the two text fields save on "Save contact".
 *
 * Corbin, 2026-10-15: shown to users as "Haul Confirmation emails" (code identifiers keep
 * "load email"). The two checkboxes sit together at the bottom, below Save contact, and the
 * first one names who it emails: the operator's SITE CONTACTS, not its billing contact.
 */

import * as React from 'react';
import { toast } from 'sonner';
import {
  CADENCE_LABEL,
  LoadEmailRequestError,
  SUMMARY_CADENCES,
  usePatchTenantSettings,
  useTenantNotificationSettings,
  type SummaryCadence,
  type TenantNotificationSettings,
} from './api';
import {
  cardStyle,
  errorText,
  helpText,
  inputStyle,
  labelStyle,
  primaryButtonStyle,
} from './styles';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function LoadEmailSettingsSection(): React.JSX.Element {
  const { data, isLoading, isError } = useTenantNotificationSettings(true);
  const patch = usePatchTenantSettings();
  const s = data?.settings;

  const [officeEmail, setOfficeEmail] = React.useState('');
  const [officePhone, setOfficePhone] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (s === undefined) return;
    setOfficeEmail(s.officeEmail ?? '');
    setOfficePhone(s.officePhone ?? '');
  }, [s]);

  const save = (payload: Partial<TenantNotificationSettings>, ok: string): void => {
    setError(null);
    patch.mutate(
      { payload },
      {
        onSuccess: () => toast.success(ok),
        onError: (err) =>
          setError(
            err instanceof LoadEmailRequestError && err.reasonFor('officeEmail') !== null
              ? 'Enter a valid office email address.'
              : 'Could not save. Try again.',
          ),
      },
    );
  };

  const saveContact = (): void => {
    const email = officeEmail.trim();
    if (email !== '' && !EMAIL_RE.test(email)) {
      setError('Enter a valid office email address.');
      return;
    }
    save(
      {
        officeEmail: email === '' ? null : email,
        officePhone: officePhone.trim() === '' ? null : officePhone.trim(),
      },
      'Office contact saved',
    );
  };

  return (
    <section aria-labelledby="load-email-defaults" style={cardStyle}>
      <h2 id="load-email-defaults" style={{ fontSize: '18px', margin: '0 0 4px' }}>
        Haul Confirmation emails
      </h2>
      <p style={helpText}>
        Defaults for every operator. Each operator can override them under Sites &amp; Operators →
        Operators → Haul Confirmation emails.
      </p>
      {isLoading && <p style={helpText}>Loading…</p>}
      {isError && (
        <p style={{ ...helpText, color: 'var(--color-danger)' }}>Could not load settings.</p>
      )}
      {s !== undefined && (
        <div style={{ display: 'grid', gap: '16px', marginTop: '16px' }}>
          <div>
            <label htmlFor="default-summary" style={labelStyle}>
              Haul Summary (default)
            </label>
            <select
              id="default-summary"
              style={inputStyle}
              value={s.defaultSummaryCadence}
              disabled={patch.isPending}
              onChange={(e): void =>
                save({ defaultSummaryCadence: e.target.value as SummaryCadence }, 'Default saved')
              }
            >
              {SUMMARY_CADENCES.map((c) => (
                <option key={c} value={c}>
                  {CADENCE_LABEL[c]}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="office-email" style={labelStyle}>
              Office email (replies go here)
            </label>
            <input
              id="office-email"
              type="email"
              style={inputStyle}
              value={officeEmail}
              onChange={(e): void => setOfficeEmail(e.target.value)}
            />
            {s.officeEmail === null && (
              <p style={helpText} data-testid="no-office-email">
                Not set — replies to Haul Confirmation emails will not reach your office.
              </p>
            )}
          </div>
          <div>
            <label htmlFor="office-phone" style={labelStyle}>
              Office phone (shown in the email footer)
            </label>
            <input
              id="office-phone"
              type="tel"
              style={inputStyle}
              value={officePhone}
              onChange={(e): void => setOfficePhone(e.target.value)}
            />
          </div>
          <div>
            <button
              type="button"
              style={primaryButtonStyle}
              disabled={patch.isPending}
              onClick={saveContact}
            >
              Save contact
            </button>
          </div>

          <div>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                minHeight: '56px',
                fontSize: '16px',
              }}
            >
              <input
                type="checkbox"
                style={{ width: '24px', height: '24px' }}
                checked={s.defaultEachHaulEnabled}
                disabled={patch.isPending}
                aria-describedby="each-haul-help"
                onChange={(e): void =>
                  save({ defaultEachHaulEnabled: e.target.checked }, 'Default saved')
                }
              />
              Email the operator&rsquo;s site contacts after each haul (default)
            </label>
            <p id="each-haul-help" style={{ ...helpText, margin: '0 0 0 36px' }}>
              Site contacts are the people you add for each operator under Sites &amp; Operators →
              Operators → Haul Confirmation emails, not the operator&rsquo;s billing contact.
            </p>
          </div>

          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              minHeight: '56px',
              fontSize: '16px',
            }}
          >
            <input
              type="checkbox"
              style={{ width: '24px', height: '24px' }}
              checked={s.requestorLoadEmailsEnabled}
              disabled={patch.isPending}
              onChange={(e): void =>
                save({ requestorLoadEmailsEnabled: e.target.checked }, 'Requestor emails saved')
              }
            />
            Email the requestor when their load is completed
          </label>
          {error !== null && (
            <p role="alert" style={errorText}>
              {error}
            </p>
          )}
        </div>
      )}
    </section>
  );
}
